import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartSimple } from '@fortawesome/pro-light-svg-icons/faChartSimple';
import { TypedMemo } from '../../common/types';
import { useGetAccountQuery } from '../../../redux/api/fymAPI';
import { openExternal } from '../../../utils/openExternal';
import { EMBED_DEFAULT_WIDTH, embedSnippetAttributes } from './embedSnippet';
import { EmbedSheetHeader } from './EmbedSheetHeader';
import { EmbedPreviewStage } from './EmbedPreviewStage';
import { EmbedWidthControl } from './EmbedWidthControl';
import { EmbedCodeBlock } from './EmbedCodeBlock';
import { EmbedSheetFooter } from './EmbedSheetFooter';
import styles from './EmbedSheet.module.scss';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

interface EmbedSheetProps {
  embedUrl: string;
  accountId: number;
  pageName: string;
  closeModal: () => void;
}

/**
 * Handing out the embed: see it, size it, read it, copy it.
 *
 * The width is the only choice the sheet offers, and it drives both the
 * preview and the snippet — the frame on the left is the page the code on the
 * right points at, at the same size.
 */
const EmbedSheetComponent: React.FC<EmbedSheetProps> = ({ embedUrl, accountId, pageName, closeModal }) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const [width, setWidth] = useState<number>(EMBED_DEFAULT_WIDTH);
  const { data: accountData } = useGetAccountQuery({ accountId });
  const artistName = accountData?.account.name ?? null;

  // The frame's accessible name. Without an artist it is just the page — a
  // guessed name would end up in somebody else's HTML.
  const title = artistName !== null && artistName !== '' ? `${pageName} — ${artistName}` : pageName;
  const subject = artistName !== null && artistName !== '' ? `${pageName} · ${artistName}` : pageName;

  const attributes = useMemo(() => embedSnippetAttributes({ embedUrl, width, title }), [embedUrl, width, title]);

  const openEmbed = useCallback(() => {
    openExternal(withLang(embedUrl)).catch((e) => console.log(e));
  }, [embedUrl, withLang]);

  return (
    <>
      <EmbedSheetHeader subject={subject} onClose={closeModal} />
      <div className={styles.body}>
        <EmbedPreviewStage embedUrl={embedUrl} width={width} title={title} />
        <div className={styles.controls}>
          <EmbedWidthControl width={width} onChange={setWidth} />
          <EmbedCodeBlock attributes={attributes} />
          <p className={styles.fineprint}>
            <FontAwesomeIcon icon={faChartSimple} />
            {intl.formatMessage({
              id: 'app.sml.embed.analytics',
              defaultMessage: 'Views and clicks inside the widget count in this page’s analytics.',
            })}
          </p>
        </div>
      </div>
      <EmbedSheetFooter onOpenEmbed={openEmbed} onDone={closeModal} />
    </>
  );
};

export const EmbedSheet = TypedMemo(EmbedSheetComponent);
